import { Injectable, computed, signal } from '@angular/core';
import { TourDefinition, TourStep } from '../models/guided-tour.model';

const STORAGE_PREFIX = 'wg-tour-done';

/**
 * The guided-tour engine. Holds the active tour's steps and cursor as signals;
 * GuidedTourComponent (mounted once in the layout shell) renders the spotlight
 * and tooltip from them. Completion — finish OR skip, we never re-nag — is
 * persisted per user per tour id.
 *
 * Two ways to start a tour:
 *  - start(tour): immediate, for callers whose anchors are already on screen.
 *  - requestStart(tour): deferred hand-off for cross-page starts (e.g. the
 *    What's New modal on /workspace launching the home-screen tour). The host
 *    page watches pendingStart() and calls startPending() once its anchors are
 *    rendered — see AgentHomeChatComponent, which also expands the sidebar
 *    first so the anchors exist.
 */
@Injectable({ providedIn: 'root' })
export class GuidedTourService {
  private readonly _tour = signal<TourDefinition | null>(null);
  private readonly _steps = signal<TourStep[]>([]);
  private readonly _index = signal(0);
  private readonly _pendingStart = signal<TourDefinition | null>(null);

  readonly active = computed(() => this._tour() !== null);
  readonly steps = this._steps.asReadonly();
  readonly index = this._index.asReadonly();
  readonly current = computed<TourStep | null>(() =>
    this._steps()[this._index()] ?? null);
  readonly isLast = computed(() => this._index() === this._steps().length - 1);
  readonly pendingStart = this._pendingStart.asReadonly();

  hasCompleted(tourId: string): boolean {
    try { return localStorage.getItem(this.storageKey(tourId)) === '1'; } catch { return false; }
  }

  /**
   * Start a tour now. Steps whose data-tour anchor isn't in the DOM are
   * dropped (permission-gated items). No-ops when already completed (unless
   * forced), already running, or nothing anchors. Returns whether it started.
   */
  start(tour: TourDefinition, opts?: { force?: boolean }): boolean {
    if (this.active()) return false;
    if (!opts?.force && this.hasCompleted(tour.id)) return false;
    const steps = tour.steps.filter(s =>
      !s.anchor || document.querySelector(`[data-tour="${s.anchor}"]`) !== null);
    // A tour that lost every spotlight step is just two bare cards — skip it.
    if (steps.filter(s => s.anchor).length === 0) return false;
    this._steps.set(steps);
    this._index.set(0);
    this._tour.set(tour);
    return true;
  }

  /** Queue a tour for the page that owns its anchors to start (see class doc). */
  requestStart(tour: TourDefinition): void {
    if (this.active()) return;
    this._pendingStart.set(tour);
  }

  /** Host-page half of requestStart: call once anchors are on screen. */
  startPending(): boolean {
    const tour = this._pendingStart();
    if (!tour) return false;
    this._pendingStart.set(null);
    return this.start(tour, { force: true });
  }

  next(): void {
    if (this.isLast()) { this.finish(); return; }
    this._index.update(i => i + 1);
  }

  back(): void {
    this._index.update(i => Math.max(0, i - 1));
  }

  /** Close early. Still marks the tour done — skipping is an answer, not a
   *  request to be asked again next session. */
  skip(): void { this.end(); }

  finish(): void { this.end(); }

  private end(): void {
    const tour = this._tour();
    if (!tour) return;
    try { localStorage.setItem(this.storageKey(tour.id), '1'); } catch { /* storage unavailable */ }
    this._tour.set(null);
    this._steps.set([]);
    this._index.set(0);
  }

  private storageKey(tourId: string): string {
    let email = '';
    try { email = (sessionStorage.getItem('current-email') ?? '').toLowerCase(); } catch { /* noop */ }
    return `${STORAGE_PREFIX}::${tourId}::${email || 'anon'}`;
  }
}
